import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { t } from 'i18next'
import moment from 'moment-timezone'

import EmptyPage from '~/components/empty-page'
import ListPagination from '~/components/ListPagination'
import Loading from '~/components/Loading'
import LoadingPopUp from '~/components/LoadingPopUp'
import Table from '~/components/Table'
import { formatSearchString, isAdvertiser } from '~/helpers'
import { useAlert } from '~/hooks/useAlert'
import useFetchSWR from '~/hooks/useFetchSWR'
import { useShowWalletOriginsColumn } from '~/modules/retailMedia/hooks/useShowWalletOrigins'
import { useWalletByPair } from '~/modules/retailMedia/hooks/useWalletByPair'
import { reatailmediaApi } from '~/modules/retailMedia/services/api'
import { buildPairTransactionQuery } from '~/modules/retailMedia/services/checking-account/buildPairTransactionQuery'
import { useAppSelector } from '~/store/hooks'

import {
  setPageIndustryListTransaction,
  setPerPageIndustryListTransaction
} from '../../store/industry/wallet/actions'
import { buildOriginByIdMap } from '../WalletBalanceOrigin/mapOrigins'
import activityStyles from '../WalletTableTransaction/walletActivitySection.module.css'
import { WalletTransactionFilters } from '../WalletTableTransaction/WalletTransactionFilters'
import { schema } from './schema'

export const IndustryMovimentationsTable = () => {
  const dispatch = useDispatch()
  const showWalletOrigins = useShowWalletOriginsColumn()
  const { wallet, mappedOrigins } = useWalletByPair()
  const [originId, setOriginId] = useState('')

  const { startDate, endDate } = useAppSelector(state => state.datepickerHeader)
  const { walletType } = useAppSelector(state => state.filters.retailMedia)
  const {
    industry: {
      wallet: { listTransaction: { page = 1, perPage = 50 } = {} }
    }
  } = useAppSelector(state => state.retailMedia)

  const isAdvertiserAccount = isAdvertiser()
  const counterpartId = isAdvertiserAccount
    ? wallet?.publisher_id
    : wallet?.advertiser_id
  const originById = useMemo(
    () => buildOriginByIdMap(mappedOrigins),
    [mappedOrigins]
  )

  useEffect(() => {
    dispatch(setPageIndustryListTransaction(1))
  }, [dispatch, originId, walletType])

  const queryParams = useMemo(
    () =>
      formatSearchString(
        buildPairTransactionQuery({
          isAdvertiserAccount,
          counterpartId,
          checkingAccountId: originId || null,
          page,
          quantity: perPage,
          transactionType: Array.isArray(walletType)
            ? undefined
            : walletType?.value,
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD')
        })
      ),
    [
      counterpartId,
      endDate,
      isAdvertiserAccount,
      originId,
      page,
      perPage,
      startDate,
      walletType
    ]
  )

  const { data, error, isValidating } =
    useFetchSWR<CheckingAccountTransactionsResponse>({
      url: `${reatailmediaApi}/checking-account/transaction?${queryParams}`,
      shouldFetch: Boolean(counterpartId),
      refreshInterval: 60000,
      revalidateOnFocus: false
    })

  const [AlertOnError] = useAlert(error)

  const handlePagination = useCallback(
    (nextPage: number) => {
      dispatch(setPageIndustryListTransaction(nextPage))
    },
    [dispatch]
  )

  const handleItemsPerPageChange = useCallback(
    ({ value }: { value: number }) => {
      dispatch(setPerPageIndustryListTransaction(value))
    },
    [dispatch]
  )

  return (
    <>
      <WalletTransactionFilters
        origins={mappedOrigins}
        originId={originId}
        onOriginChange={setOriginId}
        showOriginFilter={showWalletOrigins}
      />

      <Loading status={!data && isValidating}>
        {t('common:actions.loadingData')}...
      </Loading>

      <LoadingPopUp isActive={isValidating} />

      <AlertOnError />

      {data?.total === 0 && (
        <EmptyPage
          title={t('common:table.empty')}
          subtitle={t('common:table.emptyFiltered')}
        />
      )}

      {!!data?.total && (
        <>
          <div className={activityStyles.tableScroll}>
            <Table
              schema={schema({
                originById,
                showOriginColumn: showWalletOrigins
              })}
              data={data?.data || []}
              total={data.total}
              sideBorder
            />
          </div>

          <footer>
            <ListPagination
              total={data.total}
              label={t(
                `rm:wallet.movimentations.pagination.label.${data.total > 1 ? 'plural' : 'singular'}`
              )}
              currentPage={page}
              itemsPerPage={perPage}
              menuPlacement="top"
              onClickPagination={handlePagination}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </footer>
        </>
      )}
    </>
  )
}
